import { useQuery } from './api'
import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import type { IdNumber, Resource } from '../types/_general.js'
import type { ReferenceObject, SchemaObject } from 'openapi-typescript'
import { referenceToResource, isReference as isReferencePropName } from '../utils/reference.js'
import type { ReferenceableResource } from '../utils/reference.js'
import type { SwaggerPaths } from '@/types/swagger-paths.js'

export function useResourceSwaggerDefinition(resource: MaybeRefOrGetter<Resource | undefined>) {
  return useQuery(
    '/swagger-definition/{resource}',
    computed(() => ({ path: { resource: toValue(resource) ?? 'cases' } })),
    { enabled: computed(() => toValue(resource) != null) }
  )
}

export function useSwaggerXData() {
  return useQuery('/swagger-x-data', {})
}

export function useSwaggerPaths() {
  return useQuery('/swagger-paths', {})
}

/**
 * Strip trailing numeric segments from a dotted property path so e.g. `foo.bar.0`
 * is treated like `foo.bar`. Useful for translation lookups, reference-name
 * derivation, and other places where the array index carries no meaning.
 */
export function stripArrayIndices(path: string): string {
  const segments = path.split('.')
  while (/^\d+$/.test(segments.at(-1) ?? '')) {
    segments.pop()
  }
  return segments.join('.')
}

const allowedFileTypesRegex = /#allowed:(?<types>(?:[^\s|]+\|?)+)/
const masksRegex = /#mask:(?<masks>(?:[^\s|]+\|?)+)/
const formatAsRegex = /#format_as_(?<format>[a-z_]+)(?::(?<param>[^\s]+))?/
const actionGradeRegex = /#action_grade:(?<action_grade>major|minor)/
const itemComponentRegex = /#item_component:(?<component>\S+)/

type IdentityNumberMode = 'person' | 'company' | 'foreign'

const idNumberModeMap = {
  id_number: {},
  customer_id_number: {},
  debtor_id_number: {},

  personal_number: { mode: 'person' },
  coordination_number: { types: ['swedish_coordination_person'] },

  organization_no: { mode: 'company' },
  organization_number: { mode: 'company' },

  other_company: { types: ['other_company'] },
  other_person: { types: ['other_person'] },
  swedish_company: { types: ['swedish_company'] },
  swedish_person: { types: ['swedish_person'] },
  swedish_coordination_person: { types: ['swedish_coordination_person'] },

  foreign: { mode: 'foreign' },
} satisfies Record<string, { mode?: IdentityNumberMode; types?: IdNumber[] }>
type IdNumberProp = keyof typeof idNumberModeMap
const idNumberProps = Object.keys(idNumberModeMap) as IdNumberProp[]

const pathParamToResourceCache = new Map<string, ReferenceableResource | null>()
function swaggerRefPathToResource(
  refPath: string,
  swaggerPaths: SwaggerPaths | undefined
): ReferenceableResource | null {
  if (swaggerPaths == null) return null

  const refIdPathParam = refPath.split('/').at(-1)?.toLowerCase().replace(/__id$/, 'Id')
  if (refIdPathParam == null) return null

  const cachedResource = pathParamToResourceCache.get(refIdPathParam)
  if (cachedResource !== undefined) return cachedResource

  const refPathRegex = new RegExp(`^/(?<resource>[^/]+?)/{${refIdPathParam}}$`)
  for (const path of Object.keys(swaggerPaths)) {
    const match = refPathRegex.exec(path)
    if (match?.groups?.resource != null) {
      const resource = match.groups.resource as ReferenceableResource
      pathParamToResourceCache.set(refIdPathParam, resource)
      return resource
    }
  }

  pathParamToResourceCache.set(refIdPathParam, null)
  return null
}

// Unhandled tags in the schema: #currency (covered by enum)
export function getPropertyInfo(
  resourceSchema: SchemaObject | ReferenceObject | undefined,
  propertyPath: string,
  swaggerPaths?: SwaggerPaths
) {
  let propSchema: SchemaObject | ReferenceObject | undefined
  try {
    propSchema = resourceSchema != null ? getPropertySchema(resourceSchema, propertyPath) : undefined
  } catch {
    propSchema = undefined
  }

  // Resolve parent schema for required-field checking
  let parentSchema: SchemaObject | undefined
  let leafName: string | undefined
  if (resourceSchema != null) {
    const segments = propertyPath.split('.')
    leafName = segments.at(-1)
    if (leafName != null) {
      if (segments.length === 1) {
        parentSchema = isReferenceObject(resourceSchema) ? undefined : resourceSchema
      } else {
        try {
          const parent = getPropertySchema(resourceSchema, segments.slice(0, -1).join('.'))
          parentSchema = isReferenceObject(parent) ? undefined : parent
        } catch {
          parentSchema = undefined
        }
      }
    }
  }

  const description = !isReferenceObject(propSchema) ? propSchema?.description : undefined
  const isMediaFile = description?.includes('#media_file')
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const isTimestamps = ['_created', '_updated'].includes(propertyPath.split('.').at(-1)!)

  const formatMatch = description?.match(formatAsRegex)
  const formatTag = formatMatch?.groups?.format
  const formatParam = formatMatch?.groups?.param
  const deadlinePrefix: 'expires' | 'plain' | 'scheduled' | undefined =
    formatTag === 'deadline' && (formatParam === 'expires' || formatParam === 'plain' || formatParam === 'scheduled')
      ? formatParam
      : undefined

  // Wrapped in an IIFE to avoid manual typing, this allows us to infer the type of `format` based on the return stmnts
  const format = (() => {
    if (isReferenceObject(propSchema)) {
      return 'reference'
    } else if (propSchema?.type === 'number' || propSchema?.type === 'integer') {
      if ('enum' in propSchema) return 'enum'
      else if (formatTag === 'currency') return 'currency'
      else if (formatTag === 'string') return 'number-as-string'
      else if (description?.includes('#slider')) return 'slider'
      else return 'number'
    } else if (propSchema?.type === 'string') {
      if (propSchema.format === 'date' || propSchema.format === 'date-time') {
        if (formatTag === 'deadline') return 'deadline'
        // #format_as_date / #format_as_datetime override the schema format,
        // letting the API hide irrelevant timestamps per field (ADA-4247)
        if (formatTag === 'date') return 'date'
        if (formatTag === 'datetime') return 'date-time'
        // _created/_updated arrive here via getPropertySchema's synthesized
        // date-time schema — display them as plain dates (ADA-4247)
        if (isTimestamps) return 'date'
        return propSchema.format
      } else if (propSchema.format != null) {
        return propSchema.format as 'uri' | 'objectid'
      } else if ('enum' in propSchema) return 'enum'
      else if (description?.includes('#text_area')) return 'textarea'
      else if (description?.includes('#html_area')) return 'html'
      else if (description?.includes('#password')) return 'password'
      else if (idNumberProps.includes(propertyPath.split('.').at(-1) as IdNumberProp)) return 'id-number'
      // Any field named `iban` is grouped in fours by formatFromSchema — name-based
      // like id-number, so every IBAN across the app renders consistently.
      else if (propertyPath.split('.').at(-1) === 'iban') return 'iban'
      else return 'text'
    } else if (propSchema?.type === 'boolean') {
      if ('enum' in propSchema) return 'enum'
      else return 'boolean'
    } else if (isMediaFile) {
      return 'file'
    }
  })()

  let referencedResource: ReferenceableResource | null = null
  if (format === 'reference' || format === 'objectid') {
    if (isReferenceObject(propSchema)) {
      referencedResource = swaggerRefPathToResource(propSchema.$ref, swaggerPaths)
    }
    // Fall back to deriving resource from the property path name
    if (referencedResource == null) {
      const refPath = stripArrayIndices(propertyPath).split('.').at(-1)
      if (refPath != null && isReferencePropName(refPath)) {
        referencedResource = referenceToResource(refPath)
      }
    }
  }

  let currencyPath: string | undefined
  if (format === 'currency' && resourceSchema != null) {
    const segments = propertyPath.split('.')

    type ObjectProperties = Record<string, SchemaObject | ReferenceObject>

    const getObjectProperties = (schema: SchemaObject | ReferenceObject): ObjectProperties | undefined => {
      if (isReferenceObject(schema) || schema.type !== 'object') return undefined
      return schema.properties ?? {}
    }

    const resolveAncestor = (ancestorPath: string): SchemaObject | ReferenceObject | undefined => {
      try {
        return ancestorPath === '' ? resourceSchema : getPropertySchema(resourceSchema, ancestorPath)
      } catch {
        return undefined
      }
    }

    for (let idx = segments.length - 1; idx >= 0 && currencyPath == null; idx--) {
      const ancestorPath = segments.slice(0, idx).join('.')
      const properties = getObjectProperties(resolveAncestor(ancestorPath) ?? resourceSchema)
      if (properties == null) continue
      for (const [propName, propDef] of Object.entries(properties)) {
        if (!isReferenceObject(propDef) && (propDef.description?.includes('#currency') || propName === 'currency')) {
          currencyPath = ancestorPath === '' ? propName : `${ancestorPath}.${propName}`
          break
        }
      }
    }
  }

  const idNumberProp = leafName as IdNumberProp | undefined
  const idNumberMapping =
    format === 'id-number' && idNumberProp != null
      ? (idNumberModeMap[idNumberProp] as { mode?: IdentityNumberMode; types?: IdNumber[] })
      : undefined

  return {
    format,
    identityNumberMode: idNumberMapping?.mode,
    identityNumberTypes: idNumberMapping?.types,
    enumValues: isReferenceObject(propSchema) ? undefined : (propSchema?.enum as (string | number)[] | undefined),
    allowedFileTypes:
      format === 'file' ? description?.match(allowedFileTypesRegex)?.groups?.types?.split('|') : undefined,
    masks: description?.match(masksRegex)?.groups?.masks?.split('|'),
    actionGrade: description?.match(actionGradeRegex)?.groups?.action_grade as 'major' | 'minor' | undefined,
    itemComponent: description?.match(itemComponentRegex)?.groups?.component,
    currency: formatTag === 'currency' ? formatParam : undefined,
    currencyPath,
    deadlinePrefix,
    referencedResource,
    isRequired: parentSchema != null && leafName != null ? (parentSchema.required ?? []).includes(leafName) : false,
    isReadonly: !isReferenceObject(propSchema) ? propSchema?.readOnly === true : false,
    min:
      !isReferenceObject(propSchema) && (propSchema?.type === 'number' || propSchema?.type === 'integer')
        ? propSchema.minimum
        : undefined,
    max:
      !isReferenceObject(propSchema) && (propSchema?.type === 'number' || propSchema?.type === 'integer')
        ? propSchema.maximum
        : undefined,
    minLength:
      !isReferenceObject(propSchema) && propSchema?.type === 'string'
        ? (propSchema as { minLength?: number }).minLength
        : undefined,
    maxLength:
      !isReferenceObject(propSchema) && propSchema?.type === 'string'
        ? (propSchema as { maxLength?: number }).maxLength
        : undefined,
    pattern:
      !isReferenceObject(propSchema) && propSchema?.type === 'string'
        ? (propSchema as { pattern?: string }).pattern
        : undefined,
  }
}

export function isReferenceObject(x: SchemaObject | ReferenceObject | undefined): x is ReferenceObject {
  return x != null && '$ref' in x
}

export function getPropertySchema(
  schema: SchemaObject | ReferenceObject,
  dottedPath: string
): SchemaObject | ReferenceObject {
  let propSchema: SchemaObject | ReferenceObject | undefined = schema
  const pathSegments = dottedPath.split('.')

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  if (['_updated', '_created'].includes(pathSegments.at(-1)!)) return { type: 'string', format: 'date-time' }
  if (pathSegments.at(-1) === '_etag') return { type: 'string' }

  while (pathSegments.length > 0) {
    // We do this check on a new iteration to allow us to return a schema that is a reference schema object
    if (isReferenceObject(propSchema)) {
      if (propSchema.$ref.endsWith('__id')) {
        propSchema = { type: 'string', format: 'objectid' }
      } else {
        throw new Error('getPropertySchema cannot resolve references', { cause: propSchema })
      }
    }

    if (propSchema.type === 'array') {
      const arraySchema: SchemaObject = propSchema
      let itemsSchema: SchemaObject | ReferenceObject | undefined = Array.isArray(arraySchema.items)
        ? arraySchema.items[0]
        : arraySchema.items

      if (typeof pathSegments[0] === 'string' && /^\d+$/.test(pathSegments[0])) pathSegments.shift()

      // Swagger lies: `enum` on an array describes valid item values, not valid array values.
      // Lift it onto items so callers navigating via `.0` see the enum constraint.
      if (
        itemsSchema != null &&
        !isReferenceObject(itemsSchema) &&
        arraySchema.enum != null &&
        itemsSchema.enum == null
      ) {
        itemsSchema = { ...itemsSchema, enum: arraySchema.enum } as SchemaObject
      }

      propSchema = itemsSchema
    } else if (propSchema.type === 'object') {
      const segment = pathSegments.shift()
      if (segment == null) break

      propSchema = propSchema.properties?.[segment]
    } else {
      throw new TypeError(
        `getPropertySchema: cannot traverse into type '${String(propSchema.type)}' at path: ${dottedPath}`
      )
    }

    if (propSchema == null) {
      throw new TypeError(`getPropertySchema failed to resolve path: ${dottedPath}`)
    }
  }
  return propSchema
}
